import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Download, Database, RefreshCw } from "lucide-react";
import { API } from "../lib/api";

const Backups = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gerando, setGerando] = useState(false);
  const [baixando, setBaixando] = useState(null);

  const fetchBackups = async () => {
    try {
      const res = await axios.get(`${API}/backups`);
      setBackups(res.data);
    } catch (error) {
      toast.error("Erro ao carregar backups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const carregar = async () => { await fetchBackups(); };
    carregar();
  }, []);

  const gerarBackup = async () => {
    setGerando(true);
    try {
      await axios.post(`${API}/backups/gerar`);
      toast.success("Backup gerado com sucesso!");
      fetchBackups();
    } catch (error) {
      toast.error("Erro ao gerar backup");
    } finally {
      setGerando(false);
    }
  };

  const baixarBackup = async (nome) => {
    setBaixando(nome);
    try {
      const res = await axios.get(`${API}/backups/${nome}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const link = document.createElement('a');
      link.href = url;
      link.download = nome;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        if (document.body.contains(link)) document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
      toast.success("Download iniciado");
    } catch (error) {
      toast.error("Erro ao baixar backup");
    } finally {
      setBaixando(null);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 KB';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDateTime = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('pt-BR');
  };

  if (loading) return <div>Carregando...</div>;

  return (
    <div className="space-y-6" data-testid="backups-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Backups</h1>
          <p className="text-gray-600 dark:text-gray-300">
            Backup completo do banco em Excel — gerado automaticamente todos os dias
          </p>
        </div>
        <Button onClick={gerarBackup} disabled={gerando} data-testid="gerar-backup-btn">
          <RefreshCw className={`mr-2 h-4 w-4 ${gerando ? 'animate-spin' : ''}`} />
          {gerando ? 'Gerando...' : 'Gerar backup agora'}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Backups disponíveis ({backups.length})
          </CardTitle>
          <CardDescription>
            Mantemos os últimos backups automaticamente. Baixe o arquivo .xlsx quando precisar.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {backups.length === 0 ? (
            <p className="p-6 text-gray-500 dark:text-gray-400">Nenhum backup disponível ainda.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Arquivo</TableHead>
                  <TableHead>Gerado em</TableHead>
                  <TableHead>Tamanho</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {backups.map((b) => (
                  <TableRow key={b.nome} data-testid={`backup-row-${b.nome}`}>
                    <TableCell className="font-medium">{b.nome}</TableCell>
                    <TableCell>{formatDateTime(b.criado_em)}</TableCell>
                    <TableCell>{formatBytes(b.tamanho)}</TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => baixarBackup(b.nome)}
                        disabled={baixando === b.nome}
                        data-testid={`download-backup-${b.nome}`}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        {baixando === b.nome ? 'Baixando...' : 'Baixar'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Backups;
